// Time conversion constants (exported for reuse in date calculations)
export const MILLISECONDS_PER_SECOND = 1000
export const SECONDS_PER_MINUTE = 60
export const MINUTES_PER_HOUR = 60
export const HOURS_PER_DAY = 24
export const MILLISECONDS_PER_DAY =
  MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY

export const formatPhoneLink = (phoneNumber: string): string => `tel:${phoneNumber.replace(/\D/g, '')}`

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) return ''

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '')

  // If it's a 10-digit US phone number, format it as (XXX) XXX-XXXX
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  // If it's a 7-digit number, format it as XXX-XXXX
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`
  }

  // If it's 11 digits and starts with 1 (US country code), format as (XXX) XXX-XXXX
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }

  // For other formats, return the original number
  return phoneNumber
}

// Helper function to extract country code from phone number using countries data
export const extractCountryCodeFromPhoneNumber = (phoneNumber: string): string => {
  try {
    const countriesData = require('@data/form/countries.json')
    const phoneDigits = phoneNumber.replace(/\D/g, '')

    // Special case for +1: Default to USA (as requested)
    if (phoneDigits.startsWith('1') && phoneDigits.length >= 10) {
      return 'USA'
    }

    // Find the country by matching the longest possible dialing code
    let bestMatch = ''
    let longestMatch = 0

    for (const country of countriesData) {
      const extension = country.extension.replace('+', '').replace('-', '')
      if (phoneDigits.startsWith(extension) && extension.length > longestMatch) {
        bestMatch = country.countryCode
        longestMatch = extension.length
      }
    }

    return bestMatch
  } catch (error) {
    console.warn('Failed to load countries data for phone country code extraction:', error)
    return 'USA' // Fallback
  }
}

// Helper function to get phone country code with fallback logic
export const getPhoneCountryCode = (phoneNumber: string | undefined, existingCountryCode: string | undefined, defaultCountryCode: string): string => {
  if (phoneNumber?.startsWith('+')) {
    return extractCountryCodeFromPhoneNumber(phoneNumber)
  }
  return existingCountryCode || defaultCountryCode
}

export const convertIso2ToIso3 = (iso2Code: string): string => {
  const code = iso2Code.toLowerCase()
  switch (code) {
    case 'us':
      return 'USA'
    case 'ca':
      return 'CAN'
    case 'es':
      return 'ESP'
    case 'fr':
      return 'FRA'
    case 'de':
      return 'DEU'
    case 'it':
      return 'ITA'
    case 'gb':
      return 'GBR'
    case 'au':
      return 'AUS'
    case 'jp':
      return 'JPN'
    case 'br':
      return 'BRA'
    case 'mx':
      return 'MEX'
    case 'in':
      return 'IND'
    case 'cn':
      return 'CHN'
    case 'kr':
      return 'KOR'
    case 'be':
      return 'BEL'
    case 'nl':
      return 'NLD'
    case 'at':
      return 'AUT'
    case 'se':
      return 'SWE'
    case 'no':
      return 'NOR'
    case 'dk':
      return 'DNK'
    case 'fi':
      return 'FIN'
    case 'pl':
      return 'POL'
    case 'cz':
      return 'CZE'
    case 'hu':
      return 'HUN'
    case 'ro':
      return 'ROU'
    case 'bg':
      return 'BGR'
    case 'hr':
      return 'HRV'
    case 'si':
      return 'SVN'
    case 'sk':
      return 'SVK'
    case 'lt':
      return 'LTU'
    case 'lv':
      return 'LVA'
    case 'ee':
      return 'EST'
    case 'ie':
      return 'IRL'
    case 'pt':
      return 'PRT'
    case 'gr':
      return 'GRC'
    case 'cy':
      return 'CYP'
    case 'mt':
      return 'MLT'
    case 'lu':
      return 'LUX'
    case 'ch':
      return 'CHE'
    case 'gt':
      return 'GTM'
    default:
      return iso2Code.toUpperCase() // Fallback to uppercase ISO-2
  }
}

/**
 * Address verification (Voyager) sometimes returns state as "Florida-FL" or "Florida - FL".
 * Split so we can match against state dropdown data (name + 2-letter code).
 */
const splitStateProvinceLabel = (state: string): { namePart: string; trailingCode: string | null } => {
  const t = state?.trim() || ''
  if (!t) return { namePart: '', trailingCode: null }
  const m = t.match(/^(.+?)[\s\-–]+([A-Za-z]{2})\s*$/)
  if (m?.[1] && m?.[2]) {
    const left = m[1].trim()
    const code = m[2].toUpperCase()
    // Voyager sometimes sends "AZ-AZ" (code duplicated); treat as a single code for lookup.
    if (left.length === 2 && left.toUpperCase() === code) {
      return { namePart: code, trailingCode: code }
    }
    return { namePart: left, trailingCode: code }
  }
  return { namePart: t, trailingCode: null }
}

const findStateByNameCaseInsensitive = (statesProvinces: any[], candidate: string) => {
  if (!candidate?.trim()) return undefined
  const c = candidate.trim().toLowerCase()
  return statesProvinces.find((s) => s.name.toLowerCase() === c)
}

export const getStateName = (statesProvinces: any[] | null | undefined, state: string): string => {
  if (!statesProvinces || !Array.isArray(statesProvinces) || !state) {
    return ''
  }

  const trimmed = state.trim()
  const upper2 = trimmed.toUpperCase()
  if (upper2.length === 2) {
    const byVal = statesProvinces.find((s) => s.value === upper2)
    if (byVal) return byVal.name
  }

  const { namePart, trailingCode } = splitStateProvinceLabel(trimmed)

  let found = statesProvinces.find((s) => s.name === trimmed)
  if (found) return found.name

  found = findStateByNameCaseInsensitive(statesProvinces, trimmed)
  if (found) return found.name

  found = statesProvinces.find((s) => s.name === namePart)
  if (found) return found.name

  found = findStateByNameCaseInsensitive(statesProvinces, namePart)
  if (found) return found.name

  if (trailingCode) {
    found = statesProvinces.find((s) => s.value === trailingCode)
    if (found) return found.name
  }

  found = statesProvinces.find((s) => s.value === trimmed)
  if (found) return found.name

  return namePart || trimmed
}

export const getCountryName = (countries: any[] | null | undefined, countryCode: string): string => {
  if (!countries || !Array.isArray(countries)) {
    return ''
  }
  return countries.find((c) => c.countryCode === countryCode)?.countryName || ''
}

export const getStateCode = (statesProvinces: any[] | null | undefined, state: string): string => {
  if (!statesProvinces || !Array.isArray(statesProvinces) || !state) {
    return ''
  }

  const trimmed = state.trim()
  const upper2 = trimmed.toUpperCase()
  if (upper2.length === 2) {
    const byVal = statesProvinces.find((s) => s.value === upper2)
    if (byVal) return byVal.value
  }

  const { namePart, trailingCode } = splitStateProvinceLabel(trimmed)

  let found = statesProvinces.find((s) => s.name === trimmed)
  if (found) return found.value

  found = findStateByNameCaseInsensitive(statesProvinces, trimmed)
  if (found) return found.value

  found = statesProvinces.find((s) => s.name === namePart)
  if (found) return found.value

  found = findStateByNameCaseInsensitive(statesProvinces, namePart)
  if (found) return found.value

  if (trailingCode) {
    found = statesProvinces.find((s) => s.value === trailingCode)
    if (found) return found.value
  }

  found = statesProvinces.find((s) => s.value === trimmed)
  if (found) return found.value

  return ''
}

/** Full state name for labels (address doctor, summaries). Handles codes, "AZ-AZ", "ARIZONA", etc. */
export const formatStateProvinceForDisplay = (
  statesProvinces: any[] | null | undefined,
  raw: string | undefined,
): string => {
  const s = raw?.trim() ?? ''
  if (!s) return ''
  const name = getStateName(statesProvinces, s)
  return name || s
}

// Synchronous version for use in React components
export const formatPhoneNumberWithCountry = (phoneNumber: string | null | undefined, countryCode: string | null | undefined): string => {
  if (!phoneNumber) return ''

  // Extract country code from phone number if it starts with +
  let actualCountryCode = countryCode?.toUpperCase() || ''
  if (phoneNumber.startsWith('+')) {
    try {
      // Import the countries data synchronously (this will be bundled at build time)
      const countriesData = require('@data/form/countries.json')
      actualCountryCode = extractCountryCodeFromPhoneNumber(phoneNumber)
    } catch (error) {
      console.warn('Failed to load countries data for phone formatting:', error)
    }
  }

  const code = actualCountryCode
  const isUS = code === 'US' || code === 'USA' || code === 'CA' || code === 'CAN'

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '')

  if (isUS) {
    // For US/Canada: format as (XXX) XXX-XXXX without country code
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
  } else {
    // For non-US countries: show country code using countries data
    try {
      // Import the countries data to get the extension
      const countriesData = require('@data/form/countries.json')
      const country = countriesData.find(
        (c: any) =>
          c.countryCode === code ||
          c.countryCode === `${code}A` || // Handle 3-letter codes
          c.countryCode === `${code}B` ||
          c.countryCode === `${code}C` ||
          c.countryCode === `${code}D` ||
          c.countryCode === `${code}E` ||
          c.countryCode === `${code}F` ||
          c.countryCode === `${code}G` ||
          c.countryCode === `${code}H` ||
          c.countryCode === `${code}I` ||
          c.countryCode === `${code}J` ||
          c.countryCode === `${code}K` ||
          c.countryCode === `${code}L` ||
          c.countryCode === `${code}M` ||
          c.countryCode === `${code}N` ||
          c.countryCode === `${code}O` ||
          c.countryCode === `${code}P` ||
          c.countryCode === `${code}Q` ||
          c.countryCode === `${code}R` ||
          c.countryCode === `${code}S` ||
          c.countryCode === `${code}T` ||
          c.countryCode === `${code}U` ||
          c.countryCode === `${code}V` ||
          c.countryCode === `${code}W` ||
          c.countryCode === `${code}X` ||
          c.countryCode === `${code}Y` ||
          c.countryCode === `${code}Z`,
      )

      const countryPrefix = country?.extension || `+${code}`

      // If the phone number already starts with the country code, add a space after it
      if (phoneNumber.startsWith(countryPrefix)) {
        // Add a space after the country code if there isn't one already
        if (phoneNumber.charAt(countryPrefix.length) !== ' ') {
          return `${phoneNumber.slice(0, countryPrefix.length)} ${phoneNumber.slice(countryPrefix.length)}`
        }
        return phoneNumber
      }

      // Otherwise, add the country code prefix with a space
      return `${countryPrefix} ${phoneNumber}`
    } catch (error) {
      console.warn('Failed to load countries data for phone formatting:', error)
      // Fallback to simple formatting
      const countryPrefix = `+${code}`
      if (phoneNumber.startsWith(countryPrefix)) {
        if (phoneNumber.charAt(countryPrefix.length) !== ' ') {
          return `${phoneNumber.slice(0, countryPrefix.length)} ${phoneNumber.slice(countryPrefix.length)}`
        }
        return phoneNumber
      }
      return `${countryPrefix} ${phoneNumber}`
    }
  }

  // Fallback: return original number
  return phoneNumber
}

export const toTitleCase = (str: string | null | undefined): string => {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')
}

export const formatCurrency = (amount: number, minDecimals = 0, maxDecimals = 2): string => {
  if (typeof amount === 'undefined') return ''
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  })
}

/**
 * Formats an amount string as currency with dollar sign prefix.
 * Parses the string to extract numeric value and formats it as currency.
 * If the string already contains a currency symbol, it will not add another one.
 * @param amount - The amount as a string (e.g., "480.00", "$480.00", "480", "-6300.0")
 * @returns Formatted currency string with $ prefix (e.g., "$480.00"), or empty string if amount is null/empty, or original string if parsing fails
 */
export const formatAmountAsCurrency = (amount: string | null): string => {
  if (!amount) return ''

  // Check if amount already starts with a currency symbol ($, €, £, ¥, etc.)
  const hasCurrencySymbol = /^[$\u20AC\u00A3\u00A5\u20B9]/.test(amount.trim())

  const numericValue = parseFloat(amount.replace(/[^0-9.-]/g, ''))
  if (!isNaN(numericValue)) {
    const formatted = formatCurrency(numericValue, 2)
    // Only add $ prefix if currency symbol is not already present
    return hasCurrencySymbol ? formatted : `$${formatted}`
  }
  return amount
}

/** Title-case a phrase (e.g. ALL CAPS labels); keeps `vip` as `VIP`. */
export const toTitleCasePhrase = (phrase: string): string => {
  if (!phrase?.trim()) return ''
  return phrase
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (word === 'vip') return 'VIP'
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

export const formatPointsWithCommas = (points: number | string): string => {
  if (typeof points === 'string') {
    // Strip commas so values like "150,000" from the API are not truncated by parseInt
    const cleaned = points.replace(/,/g, '')
    const numPoints = parseInt(cleaned, 10)
    if (isNaN(numPoints)) return '0'
    return numPoints.toLocaleString('en-US')
  }
  if (typeof points !== 'number' || isNaN(points)) return '0'
  return points.toLocaleString('en-US')
}

// Formats the date by default into M/D/Y but it's also extendable
export const formatDate = (dateValue: string, outputFormat?: string) => {
  // Handle date strings like "2025-08-24" by parsing them as local dates
  let date: Date
  if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // For ISO date strings without time, parse as local date to avoid timezone issues
    const [year, month, day] = dateValue.split('-').map(Number)
    date = new Date(year, month - 1, day) // month is 0-based
  } else {
    // For other date formats, use the original parsing
    date = new Date(dateValue)
  }

  const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are zero-based
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const monthWord = months[date.getMonth()]
  const day = date.getDate().toString().padStart(2, '0')
  const dayInWeek = days[date.getDay()]
  const year = date.getFullYear()
  let hours = date.getUTCHours()
  const minutes = date.getUTCMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  const time = `${hours}:${minutes}${ampm}`

  switch (outputFormat) {
    case 'MM DD, YYYY, H:mm':
      return `${monthWord} ${day}, ${year}, ${time}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'W MM/DD/YYYY':
      return `${dayInWeek} ${month}/${day}/${year}`
    case 'MMM DD, YYYY':
      return `${monthWord} ${day}, ${year}`
    default:
      return `${month}/${day}/${year}`
  }
}

/**
 * Utility function to generate a cURL command for debugging HTTP requests.
 * @param method - The HTTP method (e.g., GET, POST).
 * @param url - The URL for the HTTP request.
 * @param headers - Optional headers (key-value pairs).
 * @param body - Optional body of the request.
 * @returns The generated cURL command as a string.
 */
export function generateCurlCommand(method: string, url: string, headers?: Record<string, string>, body?: string): string {
  let curlCommand = `curl -X ${method.toUpperCase()} '${url}'`

  // Add headers to the cURL command
  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      curlCommand += ` -H '${key}: ${value}'`
    })
  }

  // Add body to the cURL command
  if (body) {
    curlCommand += ` -d '${body.replace(/'/g, "\\'")}'` // Escape single quotes in the body
  }

  return curlCommand
}

/**
 * Converts display text to kebab case (lowercase with hyphens replacing spaces).
 * This is used for generating consistent data-testid values.
 *
 * @param displayText - The display text to convert
 * @returns The kebab case version of the text
 */
export const displayToKabobText = (displayText: string): string =>
  displayText
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert hyphen between lowercase and uppercase letters
    .toLowerCase()
    .replace(/\s+/g, '-')

/**
 * Converts full state names to their abbreviations.
 * @param stateFull - The full state name
 * @returns The state abbreviation or the original string if no match found
 */
/**
 * Maps a filter key to its corresponding label using the provided filter categories.
 *
 * @param filterKey - The filter key to look up
 * @param filterCategories - Array of filter categories containing filters with keys and labels
 * @returns The label for the filter key, or the key itself if not found
 */
export const getFilterLabel = (filterKey: string, filterCategories: any[]): string => {
  if (!filterCategories || filterCategories.length === 0) {
    return filterKey
  }

  // Search through all categories and their filters to find the matching key
  for (const category of filterCategories) {
    if (category.filters && Array.isArray(category.filters)) {
      const matchingFilter = category.filters.find((filter: any) => filter.key === filterKey)
      if (matchingFilter && matchingFilter.label) {
        return matchingFilter.label
      }
    }
  }

  // If no label found, return the key as fallback
  return filterKey
}

/**
 * Formats a filter object or value into a readable string representation.
 * Handles different filter formats including objects with type/values properties.
 *
 * @param filter - The filter to format (can be a string, object, or other value)
 * @param filterCategories - Optional array of filter categories for mapping keys to labels
 * @returns A string representation of the filter
 */
export const formatFilter = (filter: any, filterCategories?: any[]): string => {
  // Handle string filters - map to label if filterCategories provided
  if (typeof filter === 'string') {
    if (filterCategories) {
      return getFilterLabel(filter, filterCategories)
    }
    return filter
  }

  // Handle filter objects with type and values properties
  if (filter && typeof filter === 'object' && 'type' in filter && 'values' in filter) {
    const values = Array.isArray(filter.values) ? filter.values.join(', ') : filter.values
    return `${filter.type}: ${values}`
  }

  // For any other value, convert to string
  return String(filter)
}

export const getStateAbbreviation = (stateFull: string): string => {
  const stateMap: Record<string, string> = {
    Alabama: 'AL',
    Alaska: 'AK',
    Arizona: 'AZ',
    Arkansas: 'AR',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    Delaware: 'DE',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    'West Virginia': 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
  }

  // Normalize the input to title case for case-insensitive matching
  const normalizedState = toTitleCase(stateFull)
  return stateMap[normalizedState] || stateFull
}

/**
 * Normalizes a date string to local midnight for consistent day calculations
 * @param dateString - ISO date string or date string
 * @returns Date object set to local midnight
 */
const normalizeToLocalMidnight = (dateString: string): Date => {
  const date = new Date(dateString)
  // Create a new date with local year, month, and day at midnight
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export const calculateStartsIn = (date: string, upTo?: number) => {
  const todaysDate = normalizeToLocalMidnight(new Date().toISOString())
  const comparisonDate = normalizeToLocalMidnight(date)
  const timeDifference = comparisonDate.getTime() - todaysDate.getTime()
  const daysDifference = Math.floor(timeDifference / MILLISECONDS_PER_DAY)

  if (!upTo) {
    if (daysDifference >= 0) {
      return daysDifference
    }

    return -1
  }

  if (daysDifference > 0 && daysDifference < upTo) {
    return daysDifference
  }

  // Return -1 so we know that the date we're evaluating is too far in the future (after the limit we set) or in the past
  return -1
}

/**
 * Maps a card type to its corresponding payment icon path.
 * @param cardType - The card type (e.g., "VISA", "AMEX", "Mastercard")
 * @param withBase - Function to add base path to the icon path
 * @returns The full path to the payment icon image
 */
export const getPaymentIconPath = (cardType: string, withBase: (path: string) => string): string => {
  const PAYMENT_ICONS_BASE_PATH = '/content/images/payment-icons/'
  const cardTypeLower = cardType.toLowerCase()
  const iconMap: Record<string, string> = {
    visa: 'visa.svg',
    amex: 'amex.svg',
    'american express': 'amex.svg',
    mastercard: 'mastercard.svg',
    discover: 'discover.svg',
    'cc-visa': 'visa.svg',
    'cc-amex': 'amex.svg',
    'cc-mastercard': 'mastercard.svg',
    'cc-discover': 'discover.svg',
  }
  const iconName = iconMap[cardTypeLower] || 'visa.svg' // Default to visa if not found
  return withBase(`${PAYMENT_ICONS_BASE_PATH}${iconName}`)
}
